// LandingPage.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import { useTranslation } from "react-i18next";

// Images
import slide1 from "../../Images/1.PNG";
import slide2 from "../../Images/1.PNG";
import slide3 from "../../Images/1.PNG";
import logo from "../../Images/logo.png";
import bgImage from "../../Images/2.jpg";
import yourBackgroundImage from "../../Images/bg2.jpg";

import {
  FaCheckCircle,
  FaChartLine,
  FaRegSmileBeam,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);
  const sliderImages = [slide1, slide2, slide3];

  const faqs = t("faqList", { returnObjects: true });

  const toggleFAQ = (index) => setOpenIndex(openIndex === index ? null : index);

  return (
    <>
      {/* Language Switcher */}
      

      {/* Navbar */}
      <nav className="fixed top-0 left-0 w-full bg-white shadow-md z-40">
        <div className="max-w-screen-xl mx-auto py-3 px-12 flex justify-between items-center">
          <Link to="/">
            <img src={logo} alt="Logo" className="h-10 w-auto object-contain" />
          </Link>
          <div className="fixed top-4 right-4 z-50 flex items-center space-x-2">
  <div className="inline-flex rounded-md overflow-hidden border border-gray-300">
  {["de", "en"].map((lng, index) => (
    <button
      key={lng}
      onClick={() => i18n.changeLanguage(lng)}
      className={`px-4 py-2 text-sm font-medium transition-colors duration-200 focus:outline-none ${
        i18n.language === lng
          ? "bg-[#c9f567] text-black"
          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
      } ${
        index === 0
          ? 'rounded-l-md'
          : index === 1
          ? 'rounded-r-md'
          : ''
      }`}
    >
      {lng.toUpperCase()}
    </button>
  ))}
</div>

  <Link
    to="/login"
    className="ml-2 text-black bg-[#c9f567] font-bold px-4 py-2 rounded-lg shadow hover:brightness-95"
  >
    {t("login")}
  </Link>
</div>

          
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 h-[100vh] pb-10 px-6 md:px-24 overflow-hidden bg-cover bg-no-repeat bg-top" style={{ backgroundImage: `url(${yourBackgroundImage})` }}>
        <div className="absolute inset-0 bg-black bg-opacity-90 z-0" />
        <div className="relative z-10 max-w-7xl mx-auto grid md:grid-cols-2 items-center gap-16">
          <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="space-y-6 text-white">
            <h1 className="text-5xl md:text-6xl font-extrabold leading-tight">
              {t("heroTitle")}
            </h1>
            <p className="text-lg">{t("heroDescription")}</p>
            <Link to="/login" className="inline-block bg-[#c9f567] text-black font-bold px-8 py-3 rounded-full">
              {t("getStarted")}
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="w-full max-w-xl mx-auto rounded-xl overflow-hidden shadow-lg">
            <Swiper modules={[Autoplay, EffectFade]} autoplay={{ delay: 3000, disableOnInteraction: false }} loop effect="fade" className="rounded-xl">
              {sliderImages.map((img, idx) => (
                <SwiperSlide key={idx}>
                  <img src={img} alt={`Slide ${idx + 1}`} className="w-full h-[400px] object-cover" />
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-[#f9fbfc] py-12 px-6 md:px-24">
        <h3 className="text-3xl font-extrabold text-gray-800 text-center mb-12">{t("featuresTitle")}</h3>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-12">
          {["verified", "analytics", "reviews"].map((key, idx) => (
            <motion.div key={key} className="text-center" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.1 * idx }}>
              <div className="flex justify-center mb-4 text-4xl">
                {key === "verified" && <FaCheckCircle className="text-blue-600" />}
                {key === "analytics" && <FaChartLine className="text-purple-600" />}
                {key === "reviews" && <FaRegSmileBeam className="text-green-600" />}
              </div>
              <h4 className="text-xl font-bold mb-2">{t(`features.${key}Title`)}</h4>
              <p className="text-gray-600">{t(`features.${key}Desc`)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Google Data Section */}
      <section className="bg-white py-16 px-6 md:px-24 border-t">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl font-extrabold text-gray-800 text-center mb-12">{t("googleAccessTitle")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {["profile", "listing", "email"].map((key) => (
              <div key={key} className="bg-white shadow-md rounded-xl p-6 text-center border border-gray-100 hover:-translate-y-2 hover:shadow-lg transition-transform">
                <div className="text-4xl mb-4">{key === "profile" ? "👤" : key === "listing" ? "🏢" : "📧"}</div>
                <h4 className="text-lg font-semibold text-gray-800 mb-2">{t(`googleAccess.${key}Title`)}</h4>
                <p className="text-gray-600 text-sm">{t(`googleAccess.${key}Desc`)}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 italic text-center mt-10 max-w-2xl mx-auto">
            {t("googleAccessNote", { defaultValue: t("security.note") })}
          </p>
        </div>
      </section>

      {/* How It Works */}
      <div className="bg-[#f9fbfc] py-12 px-4 md:px-16">
        <h3 className="text-3xl font-extrabold text-gray-800 text-center mb-12">{t("howItWorks.title")}</h3>
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="absolute top-4 left-0 w-full h-0.5 border-t border-dotted border-gray-300 z-0 hidden md:block"></div>
          <div className="relative flex flex-col md:flex-row items-start justify-between gap-8 md:gap-0 z-10">
            {["step1", "step2", "step3"].map((step, idx) => (
              <div key={step} className="relative flex flex-col items-center text-center md:w-1/3">
                <div className="absolute top-4 w-5 h-5 rounded-full bg-black border-4 border-gray-300 shadow-md z-10"></div>
                <div className="mt-6 mb-3 w-0.5 h-8 bg-gray-300 z-0"></div>
                <div className="text-3xl text-[#c9f567] font-bold mb-2">{`0${idx + 1}`}</div>
                <h3 className="text-gray-900 text-base font-semibold mb-2 px-4">{t(`howItWorks.${step}Title`)}</h3>
                <p className="text-sm text-gray-600 px-6">{t(`howItWorks.${step}Desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <section className="relative bg-cover bg-center bg-no-repeat py-20 px-6 md:px-24" style={{ backgroundImage: `url(${bgImage})` }}>
        <div className="absolute inset-0 bg-black/90 z-0" />
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white space-y-6">
          <h3 className="text-3xl md:text-4xl font-extrabold">{t("ctaTitle")}</h3>
          <p className="text-lg md:text-xl">{t("ctaDescription", { defaultValue: "" })}</p>
          <a href="#" className="inline-block bg-[#c9f567] text-black font-semibold px-10 py-3 rounded-full shadow-lg">
            {t("ctaBtn")}
          </a>
        </div>
      </section>

      {/* Google Login Info */}
      <section className="bg-gray-50 py-20 px-6 md:px-24 border-t">
        <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-2xl p-10 text-center animate-fade-in space-y-6">
          <h3 className="text-3xl font-extrabold">{t("securityTitle")}</h3>
          <p className="text-gray-700 text-lg">{t("security.line1")}</p>
          <p className="text-gray-700 text-lg">{t("security.line2")}</p>
          <p className="text-gray-700 text-lg">{t("security.line3")}</p>
          <p className="text-sm text-gray-500 italic">{t("security.note")}</p>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="bg-white py-12 px-6 md:px-24 border-t">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-extrabold text-gray-800 text-center mb-12">{t("faqTitle")}</h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border rounded-lg shadow-sm">
                <button onClick={() => toggleFAQ(idx)} className="w-full flex justify-between items-center px-6 py-4 text-left font-semibold text-gray-800 hover:bg-gray-50">
                  {faq.q}
                  {openIndex === idx ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                <AnimatePresence initial={false}>
                  {openIndex === idx && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="px-6 pb-4 text-gray-700">
                      {faq.a}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-100 py-10 px-6 md:px-24 text-sm text-gray-600">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8 text-center md:text-left">
          <div>
            <h4 className="font-semibold mb-2">ListSync</h4>
            <p>{t("footerAbout")}</p>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/privacy-policy">{t("privacyPolicy", { defaultValue: "Privacy Policy" })}</Link></li>
              <li><Link to="/terms-conditions">{t("termsOfService", { defaultValue: "Terms of Service" })}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-2">{t("contactTitle", { defaultValue: "Contact" })}</h4>
            <p>{t("contactPhone", { defaultValue: "contact: ..." })}</p>
            <p>{t("contactAddress", { defaultValue: "Address: ..." })}</p>
          </div>
        </div>
        <div className="text-center text-xs text-gray-500 mt-8 border-t pt-4">
          {t("footerDisclaimer", { defaultValue: "This app uses Google APIs..." })}
        </div>
      </footer>
    </>
  );
}
